import type { FC, ReactNode } from 'react'
import type { GetServerSideProps } from 'next'
import type { EntryKind, EntryListItem, QaItem, SessionDetail, SessionLink } from '@/types'

import Link from 'next/link'
import { ArrowLeft, ArrowRight, CirclePlay, ListChecks } from 'lucide-react'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { firstParam, formatCount, setPageCache } from '@/lib/format'
import { ENTRY_KINDS, formatLabel } from '@/lib/kinds'
import { getSession } from '@/lib/kb'
import PageHeader from '@/components/kb/page-header'
import EntryCard from '@/components/kb/entry-card'
import QaCard from '@/components/kb/qa-card'
import TagChip from '@/components/kb/tag-chip'
import EmptyState from '@/components/kb/empty-state'
import { KindIcon } from '@/components/kb/kind-badge'
import { Pill } from '@/components/kb/section-heading'

interface SessionPageProps {
    session: SessionDetail
    entries: EntryListItem[]
    qa: QaItem[]
    previous: SessionLink | null
    next: SessionLink | null
}

const NeighbourLink: FC<{ link: SessionLink, direction: 'previous' | 'next' }> = ({ link, direction }): ReactNode => {
    const isNext = direction === 'next'

    return (
        <Link
            href={`/sessions/${link.slug}`}
            className={`group flex items-center gap-3 rounded-xl border-2 border-foreground bg-card p-4 shadow-brutal transition-transform hover:-translate-y-0.5 ${isNext ? 'justify-end text-right' : ''}`}
        >
            {!isNext && <ArrowLeft className='size-5 shrink-0' aria-hidden='true' />}

            <span className='min-w-0'>
                <span className='block font-mono text-[11px] font-bold uppercase tracking-widest text-muted-foreground'>
                    {isNext ? 'Next session' : 'Previous session'}
                </span>

                <span className='block truncate font-bold' data-mdlfy-cms={`sessions:${link.id}:title`}>
                    {link.title}
                </span>
            </span>

            {isNext && <ArrowRight className='size-5 shrink-0' aria-hidden='true' />}
        </Link>
    )
}

const SessionPage: FC<SessionPageProps> = ({ session, entries, qa, previous, next }): ReactNode => {
    const groups = ENTRY_KINDS
        .map(meta => ({ meta, items: entries.filter(entry => entry.kind === meta.kind) }))
        .filter(group => group.items.length > 0)

    const counts = new Map<EntryKind, number>(groups.map(group => [group.meta.kind, group.items.length]))

    return (
        <Layout>
            <Seo title={session.title} description={session.summary ?? `Summary, remedies and questions from ${session.title}.`} />

            <PageHeader
                crumbs={[
                    { label: 'Home', href: '/' },
                    { label: 'Courses', href: '/courses' },
                    ...(session.course_slug && session.course_title ? [{ label: <span data-mdlfy-cms={`courses:${session.course_id}:title`}>{session.course_title}</span>, href: `/courses/${session.course_slug}` }] : []),
                    { label: <span data-mdlfy-cms={`sessions:${session.id}:title`}>{session.title}</span> }
                ]}
                eyebrow={(
                    <div className='flex flex-wrap items-center gap-2'>
                        <Pill tone='bg-brand-blue'>
                            {formatLabel(session.format)}
                            {session.session_no !== null && <span className='font-mono'>{session.session_no}</span>}
                        </Pill>

                        {session.batch && (
                            <span className='rounded-md border-2 border-foreground bg-card px-2.5 py-1 text-xs font-bold' data-mdlfy-cms={`sessions:${session.id}:batch`}>
                                {session.batch}
                            </span>
                        )}
                    </div>
                )}
                title={<span data-mdlfy-cms={`sessions:${session.id}:title`}>{session.title}</span>}
                description={session.summary ? <span data-mdlfy-cms={`sessions:${session.id}:summary`}>{session.summary}</span> : undefined}
            >
                <div className='flex flex-col gap-4'>
                    {session.tags.length > 0 && (
                        <div className='flex flex-wrap gap-2'>
                            {session.tags.map(tag => (
                                <TagChip key={tag.id} tag={tag} size='md' />
                            ))}
                        </div>
                    )}

                    <div className='flex flex-wrap items-center gap-3'>
                        <span className='rounded-xl border-2 border-foreground bg-card px-4 py-2 font-mono text-sm font-bold shadow-brutal-sm'>
                            {formatCount(session.entry_count)} entries
                        </span>

                        <span className='rounded-xl border-2 border-foreground bg-card px-4 py-2 font-mono text-sm font-bold shadow-brutal-sm'>
                            {formatCount(session.qa_count)} questions
                        </span>

                        {session.video_url && (
                            <a
                                href={session.video_url}
                                target='_blank'
                                rel='noopener noreferrer'
                                className='inline-flex items-center gap-2 rounded-xl border-2 border-foreground bg-brand-red px-4 py-2 text-sm font-bold text-white shadow-brutal-sm transition-transform hover:translate-x-px hover:translate-y-px'
                            >
                                <CirclePlay className='size-4' aria-hidden='true' />
                                Watch on YouTube
                            </a>
                        )}
                    </div>

                    {session.source_title && (
                        <p className='text-xs text-muted-foreground'>
                            <span className='font-semibold'>Recording: </span>
                            <span data-mdlfy-cms={`sessions:${session.id}:source_title`}>{session.source_title}</span>
                        </p>
                    )}
                </div>
            </PageHeader>

            <div className='mx-auto max-w-6xl px-4 py-10 sm:px-6'>
                {session.key_points.length > 0 && (
                    <section id='key-points' aria-labelledby='key-points-heading' className='mb-12 rounded-xl border-2 border-foreground bg-brand-yellow p-6 shadow-brutal'>
                        <h2 id='key-points-heading' className='mb-4 flex items-center gap-2 text-xl font-black md:text-2xl'>
                            <ListChecks className='size-6' aria-hidden='true' />
                            Key points
                        </h2>

                        <ul className='grid gap-3 md:grid-cols-2'>
                            {session.key_points.map((point, index) => (
                                <li key={index} className='flex items-start gap-3 rounded-lg border-2 border-foreground bg-card p-3 text-sm leading-relaxed'>
                                    <span className='flex size-6 shrink-0 items-center justify-center rounded-full bg-foreground font-mono text-[11px] font-bold text-background'>
                                        {index + 1}
                                    </span>
                                    <span>{point}</span>
                                </li>
                            ))}
                        </ul>
                    </section>
                )}

                {groups.length > 1 && (
                    <nav aria-label='Entry types in this session' className='mb-10 flex flex-wrap gap-2'>
                        {groups.map(group => (
                            <a key={group.meta.kind} href={`#kind-${group.meta.kind}`} className={`inline-flex items-center gap-2 rounded-full border-2 border-foreground px-3 py-1 text-sm font-bold ${group.meta.tone}`}>
                                {group.meta.plural}
                                <span className='font-mono text-xs'>{counts.get(group.meta.kind)}</span>
                            </a>
                        ))}

                        {qa.length > 0 && (
                            <a href='#questions' className='inline-flex items-center gap-2 rounded-full border-2 border-foreground bg-card px-3 py-1 text-sm font-bold'>
                                Questions
                                <span className='font-mono text-xs'>{qa.length}</span>
                            </a>
                        )}
                    </nav>
                )}

                {entries.length === 0 && qa.length === 0 && (
                    <EmptyState title='Nothing extracted yet' text='This session is listed but its remedies and questions have not been added yet.' />
                )}

                <div className='flex flex-col gap-14'>
                    {groups.map(group => (
                        <section key={group.meta.kind} id={`kind-${group.meta.kind}`} aria-labelledby={`kind-heading-${group.meta.kind}`}>
                            <h2 id={`kind-heading-${group.meta.kind}`} className='mb-5 flex items-center gap-3 text-2xl font-black'>
                                <KindIcon kind={group.meta.kind} />
                                {group.meta.plural}
                                <span className='rounded-md border-2 border-foreground px-2 py-0.5 font-mono text-xs'>
                                    {group.items.length}
                                </span>
                            </h2>

                            <div className='grid gap-5 md:grid-cols-2 lg:grid-cols-3'>
                                {group.items.map(entry => (
                                    <EntryCard key={entry.id} entry={entry} showSource={false} showPlacement />
                                ))}
                            </div>
                        </section>
                    ))}

                    {qa.length > 0 && (
                        <section id='questions' aria-labelledby='questions-heading'>
                            <h2 id='questions-heading' className='mb-5 text-2xl font-black'>
                                Questions asked in this session
                            </h2>

                            <div className='flex flex-col gap-3'>
                                {qa.map(item => (
                                    <QaCard key={item.id} qa={item} showSource={false} />
                                ))}
                            </div>
                        </section>
                    )}
                </div>

                {(previous || next) && (
                    <nav aria-label='More sessions' className='mt-16 grid gap-4 md:grid-cols-2'>
                        <div>{previous && <NeighbourLink link={previous} direction='previous' />}</div>
                        <div>{next && <NeighbourLink link={next} direction='next' />}</div>
                    </nav>
                )}
            </div>
        </Layout>
    )
}

export const getServerSideProps: GetServerSideProps<SessionPageProps> = async ({ params, res }) => {
    const slug = firstParam(params?.slug)

    if (!slug) return { notFound: true }

    const data = await getSession(slug)

    if (!data) return { notFound: true }

    setPageCache(res)

    return { props: data }
}

export default SessionPage
